import Component from '@glimmer/component';
import { uniqueId } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import type { ComponentLike } from '@glint/template';
import { PhArrowRight, PhX } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import IconBadge, { type IconBadgeColor } from 'tio-ui/components/icon-badge';
import Chip, { type ChipIntent } from 'tio-ui/components/chip';
import Collapsible from 'tio-ui/components/collapsible';
import DisclosureToggle from 'tio-ui/components/disclosure-toggle';
import type { TaskUrgency } from 'tio-employee/utils/dashboard-kit/attention';

export type { TaskUrgency };

const TILE_CLASSES: Record<TaskUrgency, string> = {
  urgent: 'tio-bg-danger-soft tio-fg-danger',
  soon: 'tio-bg-warning-soft tio-fg-warning',
  onTrack: 'tio-bg-success-soft tio-fg-success',
  info: 'tio-bg-info-soft tio-fg-info',
};

const BADGE_COLORS: Record<TaskUrgency, IconBadgeColor> = {
  urgent: 'red',
  soon: 'amber',
  onTrack: 'green',
  info: 'blue',
};

const CHIP_INTENTS: Record<TaskUrgency, ChipIntent> = {
  urgent: 'danger',
  soon: 'warning',
  onTrack: 'success',
  info: 'default',
};

interface TaskCardBaseArgs {
  /** The row's h3 (rows sit under the panel's h2); says what needs doing or what happened. */
  title: string;
  /** One line on what to do, or (for `info`) what happened and that no action is needed. */
  description: string;
  /** Glyph in the urgency-tinted tile; picks the kind of task, not its urgency. */
  icon: ComponentLike<{ Element: SVGElement }>;
  /** Drives the tile and chip colour. */
  urgency: TaskUrgency;
  /** Already-formatted deadline text, e.g. "due 10/14"; the chip is omitted without it. */
  dueLabel?: string;
}

/**
 * A row ends in an action link, an action button, a dismiss button, a disclosure
 * chevron, or nothing; never more than one. A dismissible row must also name its
 * dismiss button. An expandable row completes its task in place, in the `task`
 * block, instead of navigating away.
 */
type TaskCardTrailingArgs =
  | {
      actionLabel?: undefined;
      actionRoute?: undefined;
      actionModel?: undefined;
      actionQuery?: undefined;
      onAction?: undefined;
      onDismiss?: undefined;
      dismissLabel?: undefined;
      isOpen?: undefined;
      onToggle?: undefined;
    }
  | {
      /** Trailing "Action →" link text. */
      actionLabel: string;
      /** Route the action link opens. */
      actionRoute: string;
      /** Dynamic segment for `@actionRoute`, when the route has one. */
      actionModel?: string | number;
      /** Query params for `@actionRoute`. */
      actionQuery?: Record<string, unknown>;
      onAction?: undefined;
      onDismiss?: undefined;
      dismissLabel?: undefined;
      isOpen?: undefined;
      onToggle?: undefined;
    }
  | {
      /** Trailing "Action →" button text. */
      actionLabel: string;
      actionRoute?: undefined;
      actionModel?: undefined;
      actionQuery?: undefined;
      /** Opens something in place (a modal, a drawer) instead of navigating. */
      onAction: () => void;
      onDismiss?: undefined;
      dismissLabel?: undefined;
      isOpen?: undefined;
      onToggle?: undefined;
    }
  | {
      actionLabel?: undefined;
      actionRoute?: undefined;
      actionModel?: undefined;
      actionQuery?: undefined;
      onAction?: undefined;
      /** Shows a dismiss (X) button, for notifications that need no action. */
      onDismiss: () => void;
      /** Accessible name for the dismiss button; the row title is added as its description. */
      dismissLabel: string;
      isOpen?: undefined;
      onToggle?: undefined;
    }
  | {
      actionLabel?: undefined;
      actionRoute?: undefined;
      actionModel?: undefined;
      actionQuery?: undefined;
      onAction?: undefined;
      onDismiss?: undefined;
      dismissLabel?: undefined;
      /**
       * Whether the `task` block is showing. Owned by the caller so the state
       * survives the row re-rendering (a re-sort, a sibling completing); rows
       * start collapsed and each toggles on its own.
       */
      isOpen: boolean;
      /** Called on a click of the row header; the caller flips `@isOpen`. */
      onToggle: () => void;
    };

interface TaskCardSignature {
  Args: TaskCardBaseArgs & TaskCardTrailingArgs;
  Blocks: {
    /** The in-place task UI (a select, an upload, a text input); rendered only with `@onToggle`. */
    task?: [];
  };
  Element: HTMLLIElement;
}

/**
 * One row of the Figma `Task Cards/horizontal` list. Renders an `<li>`, so it
 * belongs inside an `AttentionPanel` (or another `<ul>`). The action link (or
 * button) is described by the row title so repeated link text stays distinguishable.
 * With `@onToggle` a trailing disclosure button shows and hides the `task` block.
 */
export default class TaskCard extends Component<TaskCardSignature> {
  get tileClass() {
    return TILE_CLASSES[this.args.urgency];
  }

  get badgeColor() {
    return BADGE_COLORS[this.args.urgency];
  }

  get chipIntent() {
    return CHIP_INTENTS[this.args.urgency];
  }

  get actionQuery() {
    return this.args.actionQuery ?? {};
  }

  get actionModels() {
    const { actionModel } = this.args;
    return actionModel === undefined ? [] : [actionModel];
  }

  <template>
    {{#if @onToggle}}
      {{#let (uniqueId) (uniqueId) as |titleId taskId|}}
        <li ...attributes>
          <Card
            @radius="card"
            @elevation="none"
            @padding="sm"
            class="tio-col border tio-border-default"
          >
            <div class="tio-row">
              <IconBadge @icon={{@icon}} @color={{this.badgeColor}} @size="md" class="shrink-0" />
              <div class="tio-col-extra-tight tio-fill-min">
                <div class="tio-row-tight flex-wrap">
                  <h3 id={{titleId}} class="tio-text-base-semibold tio-fg-primary">{{@title}}</h3>
                  {{#if @dueLabel}}
                    <Chip
                      @intent={{this.chipIntent}}
                      @appearance="faded"
                      @size="sm"
                    >{{@dueLabel}}</Chip>
                  {{/if}}
                </div>
                <p class="tio-text-base-medium tio-fg-secondary">{{@description}}</p>
              </div>
              <Button
                @appearance="minimal"
                @size="sm"
                @ariaLabel={{if @isOpen "Collapse" "Expand"}}
                @testHook="dashboard-kit-task-card__toggle"
                class="shrink-0"
                aria-expanded={{if @isOpen "true" "false"}}
                aria-controls={{taskId}}
                aria-describedby={{titleId}}
                data-test-dashboard-kit-task-toggle
                {{on "click" @onToggle}}
              >
                <DisclosureToggle @expanded={{@isOpen}} />
              </Button>
            </div>
            <Collapsible @isOpen={{@isOpen}} id={{taskId}} data-test-dashboard-kit-task-body>
              {{yield to="task"}}
            </Collapsible>
          </Card>
        </li>
      {{/let}}
    {{else}}
      {{#let (uniqueId) as |titleId|}}
        {{! flex-wrap + the text column's minimum width: on a phone the trailing action drops to its own line instead of squeezing the copy. }}
        <li
          class="tio-row flex-wrap tio-card tio-card--radius-card tio-card--padding-sm border tio-border-default"
          ...attributes
        >
          <div class="tio-row-center h-10 w-10 shrink-0 rounded-lg {{this.tileClass}}">
            {{#let @icon as |Icon|}}
              <Icon class="tio-icon-lg" />
            {{/let}}
          </div>
          <div class="tio-col-extra-tight tio-fill-min min-w-48">
            <div class="tio-row-tight flex-wrap">
              <h3 id={{titleId}} class="tio-text-base-semibold tio-fg-primary">{{@title}}</h3>
              {{#if @dueLabel}}
                <Chip
                  @intent={{this.chipIntent}}
                  @appearance="faded"
                  @size="sm"
                >{{@dueLabel}}</Chip>
              {{/if}}
            </div>
            <p class="tio-text-base-medium tio-fg-secondary">{{@description}}</p>
          </div>
          {{#if @actionRoute}}
            {{#if @actionLabel}}
              <LinkTo
                @route={{@actionRoute}}
                @models={{this.actionModels}}
                @query={{this.actionQuery}}
                class="tio-row-tight shrink-0 tio-text-sm-semibold app-text-link"
                aria-describedby={{titleId}}
              >
                {{@actionLabel}}
                <PhArrowRight class="tio-icon-md" />
              </LinkTo>
            {{/if}}
          {{else if @onAction}}
            <Button
              @appearance="link"
              @size="sm"
              class="tio-row-tight shrink-0 tio-text-sm-semibold"
              aria-describedby={{titleId}}
              data-test-dashboard-kit-task-action
              {{on "click" @onAction}}
            >
              {{@actionLabel}}
              <PhArrowRight class="tio-icon-md" />
            </Button>
          {{/if}}
          {{#if @onDismiss}}
            <Button
              @appearance="minimal"
              @size="sm"
              @icon={{PhX}}
              @ariaLabel={{@dismissLabel}}
              class="shrink-0"
              aria-describedby={{titleId}}
              {{on "click" @onDismiss}}
            />
          {{/if}}
        </li>
      {{/let}}
    {{/if}}
  </template>
}
